import { useMemo } from "react";
import {
  DocumentIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  CreditCardIcon,
  KeyIcon,
  LightBulbIcon,
  BeakerIcon,
  QueueListIcon,
  Squares2X2Icon,
} from "@heroicons/react/20/solid";
import { DOCS_LINK } from "../../../../lib/constants";
import { useAuth } from "../../../../features/auth";
import type { SidebarMenuItem } from "../types";

/**
 * Sidebar information architecture (016 §4): grouped by what each page is *for*.
 *   ANALYZE   — Insights, Traces        (analyse evaluation results, daily)
 *   CONFIGURE — Evaluators, Context     (define how evaluation works)
 *   Manage    — Usage & Billing, API Keys
 * The /settings page is now just API keys (account/org moved to the Profile page);
 * Rate Limits folds into Usage; personal Profile + Sign out live in the avatar menu.
 */
export function useSidebarMenuItems() {
  const { isAuthenticated, isLoading } = useAuth();

  const authed = !isLoading && isAuthenticated;

  const analyzeItems = useMemo<SidebarMenuItem[]>(() => {
    if (!authed) return [];
    return [
      { name: "Insights", href: "/insights", Icon: LightBulbIcon },
      { name: "Failure Clusters", href: "/failure-clusters", Icon: Squares2X2Icon },
      { name: "Traces", href: "/traces", Icon: QueueListIcon },
    ];
  }, [authed]);

  const configureItems = useMemo<SidebarMenuItem[]>(() => {
    if (!authed) return [];
    return [
      { name: "Evaluators", href: "/evaluators", Icon: BeakerIcon },
      { name: "Context", href: "/context", Icon: DocumentTextIcon },
    ];
  }, [authed]);

  const utilityItems = useMemo<SidebarMenuItem[]>(() => {
    if (!authed) return [];
    return [
      { name: "Usage & Billing", href: "/usage", Icon: CreditCardIcon },
      { name: "API Keys", href: "/settings", Icon: KeyIcon },
    ];
  }, [authed]);

  const footerItems = useMemo<SidebarMenuItem[]>(
    () => [
      { name: "Docs", href: DOCS_LINK, Icon: DocumentIcon },
      { name: "Contact", href: "mailto:contact@composo.ai", Icon: EnvelopeIcon },
    ],
    []
  );

  return {
    analyzeItems,
    configureItems,
    utilityItems,
    footerItems,
    isLoading,
  };
}
